import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";
import PageMeta from "../components/PageMeta";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";

const projects = [
  {
    id: 1,
    title: "Fonderx Exchange",
    category: "Fintech",
    year: "2024",
    description: "A modern cryptocurrency and currency exchange platform with real-time rates, secure transactions, and intuitive trading experience.",
    services: ["Web Design", "Development", "UI/UX"],
    image: "/assets/portfolio/fonderx.png",
    link: "https://fonderx.com",
    color: "#D4FF00"
  },
  {
    id: 2,
    title: "Maple Kids Academy",
    category: "Childcare",
    year: "2024",
    description: "Professional website for a licensed childcare center serving ages 0-36 months in Maple Ridge, BC. Warm, trustworthy design for parents.",
    services: ["Web Design", "Branding", "SEO"],
    image: "/assets/portfolio/maple-kids.png",
    link: "https://mkids.ca",
    color: "#00D4FF"
  },
  {
    id: 3,
    title: "Vanguard PM",
    category: "Professional Contractor",
    year: "2024",
    description: "Professional contracting and maintenance services website. Clean, modern design showcasing their expertise and building trust with clients.",
    services: ["Web Design", "Development", "SEO"],
    image: "/assets/portfolio/vanguard-pm.png",
    link: "https://vanguardpm.ca",
    color: "#FF00D4"
  },
  {
    id: 4,
    title: "VanSign",
    category: "Signage Company",
    year: "2025",
    description: "Vancouver's premium signage company. A bold, striking website designed to elevate their business presence and generate leads with an integrated quote system.",
    services: ["Web Design", "Development", "Lead Generation"],
    image: "/assets/portfolio/vansign.png",
    link: "https://vansign.ca",
    color: "#F59E0B"
  }
];

const ProjectCard = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <div 
      className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-0 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Side */}
      <div className={`relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
        <a 
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="block relative overflow-hidden rounded-2xl group"
        >
          <div 
            className="absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: `linear-gradient(135deg, ${project.color}20 0%, transparent 100%)` }}
          />
          <img 
            src={project.image}
            alt={project.title}
            className="w-full h-auto transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute bottom-6 right-6 z-20">
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0"
              style={{ background: project.color }}
            >
              <ExternalLink size={24} className="text-black" />
            </div>
          </div>
        </a>
      </div>

      {/* Content Side */}
      <div className={`${isEven ? 'lg:order-2 lg:pl-16' : 'lg:order-1 lg:pr-16'}`}>
        <div className="flex items-center gap-4 mb-4">
          <span 
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: project.color }}
          >
            {project.category}
          </span>
          <span className="text-xs" style={{ color: '#A1A1AA' }}>— {project.year}</span>
        </div>
        
        <h2 
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 normal-case"
          style={{ fontFamily: '"Clash Display", sans-serif' }}
        >
          {project.title}
        </h2>
        
        <p className="text-base md:text-lg mb-8 leading-relaxed" style={{ color: '#A1A1AA' }}>
          {project.description}
        </p>
        
        <div className="flex flex-wrap gap-3 mb-8">
          {project.services.map((service) => (
            <span 
              key={service}
              className="px-4 py-2 text-sm rounded-full"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {service}
            </span>
          ))}
        </div>
        
        <a 
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-medium hover:gap-4 transition-all"
          style={{ color: project.color }}
        >
          View Live Site <ArrowUpRight size={18} />
        </a>
      </div>
    </div>
  );
};

export default function Portfolio() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Portfolio | Averoan Technologies"
        description="Real websites for real businesses: fintech, childcare, contracting, and signage. See what we can build for you."
      />
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pb-24 relative overflow-hidden" data-testid="portfolio-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        
        <div className="container-main relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left - Title */}
            <div className="lg:col-span-7">
              <span className="tag mb-6">Portfolio</span>
              <h1 
                className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Our <span style={{ color: '#D4FF00' }}>work</span><br />
                speaks for itself
              </h1>
              <p className="text-base md:text-xl max-w-xl" style={{ color: '#A1A1AA' }}>
                Every project tells a story. Here are some of our favorites — crafted with passion, precision, and purpose.
              </p>
            </div>
            
            {/* Right - Creative Stats */}
            <div className="lg:col-span-5">
              <div 
                className="relative p-8 rounded-3xl"
                style={{ 
                  background: 'linear-gradient(145deg, #18181B 0%, #0F0F11 100%)',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                {/* Decorative line */}
                <div 
                  className="absolute top-0 left-8 right-8 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, #D4FF00, transparent)' }}
                />
                
                <div className="space-y-6">
                  {/* Stat 1 */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#A1A1AA' }}>Projects Delivered</span>
                    <span className="text-3xl font-bold" style={{ color: '#D4FF00', fontFamily: '"Clash Display", sans-serif' }}>50+</span>
                  </div>
                  <div className="h-px w-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  
                  {/* Stat 2 */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#A1A1AA' }}>Client Satisfaction</span>
                    <span className="text-3xl font-bold" style={{ color: '#fff', fontFamily: '"Clash Display", sans-serif' }}>98%</span>
                  </div>
                  <div className="h-px w-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  
                  {/* Stat 3 */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#A1A1AA' }}>Years in Business</span>
                    <span className="text-3xl font-bold" style={{ color: '#fff', fontFamily: '"Clash Display", sans-serif' }}>5+</span>
                  </div>
                  <div className="h-px w-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
                  
                  {/* Stat 4 */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: '#A1A1AA' }}>Support</span>
                    <span className="text-3xl font-bold" style={{ color: '#D4FF00', fontFamily: '"Clash Display", sans-serif' }}>24/7</span>
                  </div>
                </div>
                
                {/* Decorative line */}
                <div 
                  className="absolute bottom-0 left-8 right-8 h-px"
                  style={{ background: 'linear-gradient(90deg, transparent, #D4FF00, transparent)' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-12 md:py-20" data-testid="portfolio-projects">
        <div className="container-main">
          <div className="space-y-24 md:space-y-32">
            {projects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 md:py-32 relative overflow-hidden"
        style={{ background: '#18181B' }}
      >
        <div className="hero-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-30" />
        
        <div className="container-main relative z-10">
          <div 
            className="rounded-3xl p-12 md:p-20 text-center relative overflow-hidden"
            style={{ background: '#0F0F11', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {/* Decorative gradient */}
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-20"
              style={{ background: 'radial-gradient(circle, #D4FF00 0%, transparent 70%)' }}
            />
            
            <div className="relative z-10">
              <span className="tag mb-6">Start Your Project</span>
              <h2 
                className="text-4xl md:text-6xl font-bold mb-6 mt-4"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Let's create something<br />
                <span style={{ color: '#D4FF00' }}>amazing together</span>
              </h2>
              <p className="text-base md:text-lg mb-10 max-w-lg mx-auto" style={{ color: '#A1A1AA' }}>
                Ready to transform your digital presence? Let's discuss your vision and bring it to life.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/consultation" className="btn-primary">
                  Get Started <ArrowUpRight size={16} />
                </Link>
                <Link to="/contact" className="btn-outline">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
